// Stage — owns the WebGPU renderer, the scene, and the post chain.
//
// Hard WebGPU requirement (engine decision): no forceWebGL, no fallback path. If the adapter probe
// or backend selection fails we throw with a clear message and the caller surfaces it.
//
// The post chain is built ONCE here: scene pass with an MRT emissive target → bloom fed by
// emissive → tone-mapped output. Per-frame work is `render()` plus uniform writes elsewhere;
// nothing in this class allocates after init.

import { bloom } from "three/addons/tsl/display/BloomNode.js";
import { emissive, mrt, output, pass } from "three/tsl";
import {
  ACESFilmicToneMapping,
  PerspectiveCamera,
  RenderPipeline,
  Scene,
  WebGPURenderer,
} from "three/webgpu";
import type { BloomConfig } from "./bloom.js";

export type StageConfig = {
  /** Bloom + tone-mapping parameters (read once at construction; mutate later via `applyBloom`). */
  bloom: BloomConfig;
  /** Camera to render with. Default: `new PerspectiveCamera(38, 1, 0.1, 600)`. */
  camera?: PerspectiveCamera;
  /** Scene to render. Default: a fresh empty `Scene` (the caller adds its objects). */
  scene?: Scene;
  /** Upper bound on `devicePixelRatio`. Default: 2. */
  pixelRatioCap?: number;
  /** MSAA on the renderer. Default: true. */
  antialias?: boolean;
};

export class Stage {
  readonly renderer: WebGPURenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  private readonly pipeline: RenderPipeline;
  private readonly bloomNode: ReturnType<typeof bloom>;
  private readonly resizeObserver: ResizeObserver;

  private constructor(
    renderer: WebGPURenderer,
    parent: HTMLElement,
    camera: PerspectiveCamera,
    scene: Scene,
    bloomConfig: BloomConfig,
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    const scenePass = pass(this.scene, this.camera);
    scenePass.setMRT(mrt({ output, emissive }));
    const color = scenePass.getTextureNode();
    const emissiveTex = scenePass.getTextureNode("emissive");
    this.bloomNode = bloom(
      emissiveTex,
      bloomConfig.strength,
      bloomConfig.radius,
      bloomConfig.threshold,
    );
    this.pipeline = new RenderPipeline(renderer, color.add(this.bloomNode));

    this.resizeObserver = new ResizeObserver(() => this.resize(parent));
    this.resizeObserver.observe(parent);
    this.resize(parent);
  }

  static async create(parent: HTMLElement, config: StageConfig): Promise<Stage> {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    const adapter = gpu === undefined ? null : await gpu.requestAdapter();
    if (adapter === null || adapter === undefined) {
      throw new Error(
        "WebGPU is required but unavailable (no navigator.gpu adapter). " +
          "Use a WebGPU-capable browser.",
      );
    }

    const renderer = new WebGPURenderer({ antialias: config.antialias ?? true });
    await renderer.init();
    const backend = renderer.backend as { isWebGPUBackend?: boolean };
    if (backend.isWebGPUBackend !== true) {
      throw new Error("Renderer did not select the WebGPU backend (hard requirement).");
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.pixelRatioCap ?? 2));
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = config.bloom.exposure;

    const camera = config.camera ?? new PerspectiveCamera(38, 1, 0.1, 600);
    const scene = config.scene ?? new Scene();
    const stage = new Stage(renderer, parent, camera, scene, config.bloom);
    parent.appendChild(renderer.domElement);
    return stage;
  }

  applyBloom(b: BloomConfig): void {
    this.bloomNode.strength.value = b.strength;
    this.bloomNode.radius.value = b.radius;
    this.bloomNode.threshold.value = b.threshold;
    this.renderer.toneMappingExposure = b.exposure;
  }

  render(): void {
    this.pipeline.render();
  }

  private resize(parent: HTMLElement): void {
    const w = Math.max(1, parent.clientWidth);
    const h = Math.max(1, parent.clientHeight);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  destroy(): void {
    this.resizeObserver.disconnect();
    this.renderer.domElement.remove();
    void this.renderer.dispose();
  }
}
